import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';

export default function MeasureGuidesPage() {
  const [guides,  setGuides]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/guides')
      .then(r => setGuides(r.data.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        <div className="bg-on-surface text-white px-6 md:px-10 py-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">Knowledge Zone</span>
            </div>
            <h1 className="font-Lato text-5xl md:text-6xl uppercase text-white">Knowledge Zone</h1>
            <p className="font-body text-gray-400 mt-4 max-w-xl leading-relaxed">
              Step-by-step measurement guides to help you select the correct fastener size for your application.
            </p>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(4)].map((_, i) => <div key={i} className="h-60 skeleton" />)}
            </div>
          ) : guides.length === 0 ? (
            <div className="text-center py-24">
              <span className="material-symbols-outlined text-[80px] text-outline-variant">straighten</span>
              <h2 className="font-headline font-black text-3xl uppercase mt-4 text-secondary">No guides available yet</h2>
              <p className="font-body text-sm text-secondary mt-2">Measurement guides will be added soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map(guide => (
                <Link key={guide._id} to={`/how-to-measure/${guide._id}`}
                  className="group bg-white card-industrial flex flex-col overflow-hidden">
                  {/* Cover image */}
                  <div className="h-48 bg-surface-container overflow-hidden relative">
                    {guide.coverImage
                      ? <img src={guide.coverImage} alt={guide.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <span className="material-symbols-outlined text-[64px] text-outline-variant">straighten</span>
                        </div>
                    }
                    {guide.category && (
                      <span className="absolute top-3 left-3 badge badge-orange text-[10px]">{guide.category}</span>
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-headline font-bold text-xl uppercase tracking-tight text-on-surface leading-tight">{guide.title}</h3>
                    {guide.intro && (
                      <p className="font-body text-xs text-secondary mt-2 line-clamp-2 leading-relaxed">{guide.intro}</p>
                    )}
                    <div className="mt-auto pt-4 flex items-center justify-between border-t border-surface-container mt-3">
                      <span className="font-label text-xs text-secondary">{guide.steps?.length || 0} step{guide.steps?.length !== 1 ? 's' : ''}</span>
                      <span className="font-label text-xs font-bold text-primary-container uppercase tracking-wider group-hover:gap-2 flex items-center gap-1 transition-all">
                        Read Guide <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

// ── Fullscreen image lightbox, reused for the cover image and every step image ──
function ImageLightbox({ url, onClose }) {
  if (!url) return null;

  const handleDownload = async () => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = url.split('/').pop().split('?')[0] || 'image.jpg';
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback: open in a new tab if the blob fetch fails (e.g. CORS)
      window.open(url, '_blank');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6"
      onClick={onClose}
    >
      {/* Top-right controls */}
      <div className="absolute top-5 right-5 flex items-center gap-3 z-10" onClick={e => e.stopPropagation()}>
        <button onClick={handleDownload}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 backdrop-blur-sm transition-colors">
          <span className="material-symbols-outlined text-[20px]">download</span>
          <span className="font-label text-xs font-bold uppercase tracking-widest">Download</span>
        </button>
        <button onClick={onClose}
          className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-colors">
          <span className="material-symbols-outlined text-[24px]">close</span>
        </button>
      </div>

      {/* Image, fit to screen without cropping */}
      <img
        src={url}
        alt=""
        onClick={e => e.stopPropagation()}
        className="max-w-full max-h-full object-contain"
      />
    </div>
  );
}

export function MeasureGuideDetailPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [guide,   setGuide]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    api.get(`/guides/${id}`)
      .then(r => setGuide(r.data.data))
      .catch(() => navigate('/how-to-measure'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-container border-t-transparent rounded-full animate-spin" />
          <p className="font-label text-sm text-secondary uppercase tracking-widest">Loading guide...</p>
        </div>
      </div>
    </div>
  );

  if (!guide) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Header */}
        <div className="bg-on-surface text-white px-6 md:px-10 py-12">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <Link to="/how-to-measure" className="hover:text-gray-300">How to Measure</Link>
              <span>/</span>
              <span className="text-gray-400">{guide.title}</span>
            </div>
            {guide.category && <span className="badge badge-orange text-[10px] mb-3">{guide.category}</span>}
            <h1 className="font-headline font-black text-4xl md:text-5xl uppercase tracking-tight text-white">{guide.title}</h1>
          </div>
        </div>

        {/* Guide content */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          {/* Cover image — click to view fullscreen */}
          {guide.coverImage && (
            <div className="relative group mb-10 cursor-zoom-in" onClick={() => setLightboxUrl(guide.coverImage)}>
              <img src={guide.coverImage} alt={guide.title}
                className="w-full max-h-80 object-contain border border-outline-variant/20" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-[36px] opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">
                  zoom_in
                </span>
              </div>
            </div>
          )}

          {/* Intro */}
          {guide.intro && (
            <div className="mb-10 p-6 bg-surface-container-low border-l-4 border-primary-container">
              <p className="font-body text-base text-on-surface leading-relaxed">{guide.intro}</p>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-10">
            {guide.steps?.sort((a, b) => a.sortOrder - b.sortOrder).map((step, i) => (
              <div key={i} className="flex gap-6 items-start">
                {/* Step number */}
                <div className="w-10 h-10 bg-primary-container text-white font-headline font-black text-lg flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-sans font-bold text-xl uppercase tracking-tight  mb-3">{step.title}</h3>
                  {step.image && (
                    <div className="relative group mb-4 cursor-zoom-in inline-block" onClick={() => setLightboxUrl(step.image)}>
                      <img src={step.image} alt={step.caption || step.title}
                        className="max-h-72 object-contain border border-outline-variant/20" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-[32px] opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">
                          zoom_in
                        </span>
                      </div>
                    </div>
                  )}
                  {step.caption && <p className="font-label text-sm font-bold text-on-surface mb-1">{step.caption}</p>}
                  {step.note   && <p className="font-body text-sm text-secondary leading-relaxed">{step.note}</p>}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-outline-variant/20 flex flex-col sm:flex-row gap-4">
            <Link to="/how-to-measure" className="btn-secondary">
              <span className="material-symbols-outlined">arrow_back</span> All Guides
            </Link>
            <Link to="/products" className="btn-primary">
              <span className="material-symbols-outlined">inventory_2</span> Browse Products
            </Link>
          </div>
        </div>
      </main>

      <Footer />

      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
}